import { SxProp, Text, TextInput, View } from 'dripsy';
import React from 'react';
import { Platform, ViewProps } from 'react-native';

import { useOtpInput } from './useOtpInput.hook';

export enum OtpInputVariant {
  Square = 'square',
  Rounded = 'rounded',
  Underline = 'underline'
}

export interface UniOtpInputProps {
  maxLength?: number;
  errorText?: string;
  enabled?: boolean;
  onReady?: (ready: boolean) => void;
  onValueChange?: (value: string) => void;
  variant?: OtpInputVariant;
  sx?: SxProp;
}

export const UniOtpInput = (props: UniOtpInputProps) => {
  const { maxLength = 6, errorText, variant = OtpInputVariant.Rounded, sx, onReady, onValueChange, enabled = true } = props;
  const { otp, inputRefs, handleChange, handleBackspace, focusedIndex, handleFocusedIndex } = useOtpInput({
    maxLength,
    onReady,
    onValueChange
  });


  return (
    <View>
      <OtpInputContainer>
        {otp.map((value, index) => {
          let inputStyles: SxProp = { ...otpInputBoxBaseStyle };
          if (focusedIndex === index) {
            inputStyles = { ...styles, ...styles.otpInputBoxFocused, ...styles.otpInputBoxFocusedHighlight };
          } else {
            inputStyles = { ...styles, ...styles.otpInputBox };
          }
          if (!enabled) {
            inputStyles = { ...inputStyles, ...styles.otpInputBoxDisabled };
          } else if (errorText) {
            inputStyles = { ...inputStyles, ...styles.otpInputBoxError };
          }
          inputStyles = { ...inputStyles, ...otpInputStyles[variant] };
          if (Platform.OS !== 'android' && Platform.OS !== 'ios') {
            inputStyles = { ...inputStyles, fontWeight: 600 };
          }
          return (
            <TextInput
              key={index}
              ref={(input) => (inputRefs.current[index] = input)}
              sx={inputStyles}
              value={value}
              onChangeText={(text) => handleChange(index, text.replace(/[^0-9]/g, ''))}
              onKeyPress={({ nativeEvent: { key } }) => key === 'Backspace' && handleBackspace(index)}
              keyboardType="number-pad"
              returnKeyType="next"
              editable={enabled}
              onFocus={() => handleFocusedIndex(index)}
              onBlur={() => handleFocusedIndex(-1)}
            />
          );
        })}
      </OtpInputContainer>
      {errorText ? <Text sx={styles.errorText}>{errorText}</Text> : <></>}
    </View>
  );
};

const OtpInputContainer = (props: ViewProps) => {
  return (
    <View {...props} sx={styles.otpInputContainer}>
      {props.children}
    </View>
  );
};

const otpInputBoxBaseStyle: SxProp = {
  width: 45,
  height: 48,
  borderWidth: 1,
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  outlineStyle: 'none',
  fontSize: 16,
  lineHeight: 19
};

const otpInputStyles: Record<OtpInputVariant, SxProp> = {
  square: {},
  rounded: {
    borderRadius: 5
  },
  underline: {
    borderWidth: 0,
    borderBottomWidth: 1
  }
};

const styles: Record<string, SxProp> = {
  errorText: {
    marginTop: 4,
    fontSize: 12,
    color: '$danger'
  },

  otpInputContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 15
  },
  otpInputBox: {
    ...otpInputBoxBaseStyle,
    borderColor: '$borderField'
  },
  otpInputBoxFocused: {
    ...otpInputBoxBaseStyle,
    borderColor: '$borderField'
  },
  otpInputBoxFocusedHighlight: {
    borderColor: '$secondary'
  },
  otpInputBoxError: {
    ...otpInputBoxBaseStyle,
    borderColor: '$danger'
  },
  otpInputText: {
    fontSize: '$3',
    textAlign: 'center',
    color: '$textPrimary'
  },
  otpInputBoxDisabled: {
    ...otpInputBoxBaseStyle,
    borderColor: '$disable'
  }
};